import { useState } from "react";
import {
  Button,
  Container,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  Input,
  Modal,
  FormGroup,
  Form,
  Alert,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import AppSpinner from "../../../elements/AppSpinner";
import AppHelmet from "../../../components/AppComponents/AppHelmet";
import BasicTableView from "../CloudListView/BasicTableView";
import API from "../../../api/api/API";
import useGlobalLists from "../../../api/api-hooks/cloud/useGlobalLists";
import { useQueryClient } from "@tanstack/react-query";
import { List } from "../GlobalLists/GlobalVariableListDetailView";
import StrUtils from "../../../utils/StrUtils";
import RequiredTitle from "../../../elements/RequiredTitle";

const GlobalVariableListsView = ({ urlStart }: { urlStart?: string }) => {
  const { globalLists: lists, globalListsLoading: loading } = useGlobalLists();
  const [name, setName] = useState("");
  const [apiName, setAPIName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const navigate = useNavigate();
  const client = useQueryClient();
  urlStart = urlStart || "/supportal";

  const handleNameChange = (name: string) => {
    handleAPINameChange(name);
    setName(name);
  };

  const handleAPINameChange = (name: string) => {
    const cleanAPIName = StrUtils.cleanAPIName(name);

    setAPIName(cleanAPIName);
  };

  const handleSetError = (name: any, val = null) => {
    if (!val) return setError("");

    setError(val);

    setTimeout(() => {
      setError(name);
    }, 4000);
  };

  const create = (e: any) => {
    e.preventDefault();

    setSubmitting(true);

    API.createGlobalVariableList(name, apiName, description)
      .then(
        (data) => {
          client.setQueryData<List[]>(["globalLists"], (prevLists) => {
            return [...(prevLists || []), data.data.data.list];
          });

          setDescription("");
          setName("");
          setCreateModal(false);
        },
        (error) => {
          if (error.response && error.response.status === 409) {
            handleSetError(
              "A list with that Name already exists. Modify Name to continue."
            );
          } else {
            handleSetError(
              "We were unable to create the list. Please try again."
            );
          }
          console.error(error);
        }
      )
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <>
      <AppHelmet title="Global Variable Lists - GrowthCloud.com" />
      <div className="header pb-6">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col lg="auto" md="12">
                <h6 className="h2 mb-0">Global Variable Lists</h6>{" "}
                <Breadcrumb
                  className="d-none d-md-block mt-1"
                  listClassName="breadcrumb-links"
                >
                  <BreadcrumbItem>
                    <Link to={urlStart}>
                      <i className="mdi mdi-rocket" />
                    </Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem>Global Variables</BreadcrumbItem>
                  <BreadcrumbItem>Lists</BreadcrumbItem>
                </Breadcrumb>
              </Col>
              <Col
                className="text-lg-right mt-3 mt-lg-0"
                lg=""
                md="12"
                style={{
                  overflowY: "scroll",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                <Button color="primary" onClick={() => setCreateModal(true)}>
                  New
                </Button>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
      <Container className="mt--6" fluid>
        <hr className="mt-0 mb-4"></hr>
        <BasicTableView
          items={lists || []}
          loading={loading}
          getItemTitle={(l) => l?.Name}
          getItemSubtitle={(l) => l?.Description || "No description provided."}
          title={"Name / Description"}
          onClick={(l) => {
            navigate(`${urlStart}/global/variables/lists/${l.APIName}`);
          }}
        />
      </Container>
      <Modal
        className="modal-dialog-centered"
        isOpen={createModal}
        toggle={() => setCreateModal(false)}
      >
        <div className="modal-header">
          <h5 className="modal-title" id="exampleModalLabel">
            Create Variable List
          </h5>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => setCreateModal(false)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <Form onSubmit={create}>
          <div className="modal-body">
            <FormGroup>
              <RequiredTitle title="Name" />
              <Input
                placeholder="Name"
                type="text"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <RequiredTitle title="API Name" />
              <Input
                placeholder="API Name"
                type="text"
                value={apiName}
                onChange={(e) => handleAPINameChange(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <h5>Description</h5>
              <Input
                placeholder="Description"
                type="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FormGroup>
            {error ? (
              <Alert color="warning" className="mt-3 mb-0">
                {error}
              </Alert>
            ) : null}
          </div>
          <div className="modal-footer">
            <Button
              color="secondary"
              data-dismiss="modal"
              type="button"
              onClick={() => setCreateModal(false)}
            >
              Close
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={submitting || !name}
            >
              {submitting ? (
                <AppSpinner style={{ height: 20, width: 20 }} color="white" />
              ) : (
                <>Create</>
              )}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default GlobalVariableListsView;
